import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Box, Card, Divider, Stack, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import { CreateAlgoRequest } from "../lazertrader/lazerApi";
import {
  Algo,
  AlgoConfiguration,
  LimitOrderConfiguration,
  TerminatorConfiguration,
} from "../lazertrader/types";
import { BuySellToggle } from "./formik/buySellToggle";
import { SubmitButton, useSubmit, useSubmitBulk } from "./formik/submitButton";
import { GridField, ProductNameField } from "./formik/configTabTextField";
import { AdvancedField } from "./formik/advancedField";
import { QuantityField } from "./formik/quantityField";
import { PriceField } from "./formik/priceField";
import { ReferencePriceField } from "./formik/referencePriceField";
import { ShimuraiPriceField } from "./formik/shimuraiPriceField";
import { BehaviorSwitch, TerminatorSwitch } from "./formik/behaviorSwitch";
import { Behavior, behaviorAlgoSpec, isShimuraiAlgoSpec } from "./algoSpec";
import { Advanced, AdvancedToggle } from "./formik/advanced";
import { getUserInitials, useGraphService } from "../../Services/GraphService";
import { useListedAlgo } from "../lazertrader/contexts/listAlgosContext";
import { Form } from "./formik/form";
import { ConfigSidebarMinWidth, ConfigTabPaper } from "./formik/configTabPaper";
import {
  initialState,
  ConfigTabState,
  customResetForm,
  isLinearCloser,
} from "./types";
import {
  usePrefillFromOrderbook,
  useSelectedAlgo,
  useShimuraiSelection,
} from "./hooks";
import { SMALL_INPUT_HEIGHT } from "./formik/constants";
import { useClearPrefillOnClickOutside } from "./useConfigPopup";
import dayjs from "dayjs";

import { Price } from "../../price";
import { RelativeTimeInput } from "./formik/time/relativeTimeInput";
import { CancelButton } from "./formik/cancelButton";
import { BalancingGroupField } from "./formik/balancingGroupField";
import {
  LINEAR_CLOSER_FIELDS_MIN_WIDTH,
  LinearCloserFields,
} from "./formik/linearCloserFields";
const ConfigTab = () => {
  const graphService = useGraphService();
  const { selectedAlgos } = useListedAlgo();
  const shimurai = useShimuraiSelection();
  const submit = useSubmit();
  const submitBulk = useSubmitBulk();

  return (
    <Form
      initialValues={initialState}
      onSubmit={(values: ConfigTabState, { resetForm }) => {
        // Only a *bulk* shimurai selection takes the list route. One algo —
        // shimurai or not — is a single update built from the whole form, which
        // is what `toAlgo` does.
        if (shimurai?.bulk) {
          submitBulk(
            () => customResetForm(values.behavior, resetForm),
            shimurai.algos.map((algo) => toMarginOverrideAlgo(algo, values))
          );
          return;
        }

        const initials = getUserInitials(graphService.user);
        const algo = toAlgo(values, initials, selectedAlgos);
        submit(
          () => customResetForm(values.behavior, resetForm),
          algo,
          Boolean(values.algo_id)
        );
      }}
    >
      <Inner />
    </Form>
  );
};

const Inner = () => {
  const quantityFieldRef = useRef<HTMLInputElement>(null);
  const marginFieldRef = useRef<HTMLInputElement>(null);
  usePrefillFromOrderbook(quantityFieldRef);
  useSelectedAlgo(quantityFieldRef, marginFieldRef);
  const shimurai = useShimuraiSelection();
  const { addPreventClearAlgosRef, removePreventClearAlgosRef } =
    useListedAlgo();
  const { values } = useFormikContext<ConfigTabState>();

  const [advancedOpen, setAdvancedOpen] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  useClearPrefillOnClickOutside(ref);

  useEffect(() => {
    addPreventClearAlgosRef(ref);
    return () => removePreventClearAlgosRef(ref);
  }, [addPreventClearAlgosRef, removePreventClearAlgosRef]);

  return (
    <Box
      ref={ref}
      sx={{
        minWidth: ConfigSidebarMinWidth,
        flexShrink: 0,
        display: "flex",
      }}
    >
      <Card
        elevation={12}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: (t) => t.palette.background.paper,
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            padding: "2px 4px 2px 10px",
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontSize: 12 }}>
            {shimurai?.bulk
              ? `Override margin — ${shimurai.algos.length} shimurai algos`
              : values.algo_id
                ? "Edit algo"
                : "New algo"}
          </Typography>
        </Stack>
        <ConfigTabPaper>
          <Stack spacing={1} direction="column">
            <AlgoFields
              quantityFieldRef={quantityFieldRef}
              marginFieldRef={marginFieldRef}
              shimurai={
                shimurai !== undefined ||
                values.behavior === Behavior.STEP_SHIMURAI
              }
              bulk={shimurai?.bulk ?? false}
              editing={Boolean(values.algo_id)}
              advancedOpen={advancedOpen}
              linearCloser={isLinearCloser(values)}
            />
            <Stack
              direction="row"
              spacing={1}
              sx={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <AdvancedToggle
                open={advancedOpen}
                onToggle={() => setAdvancedOpen((open) => !open)}
              />
              <Box sx={{ display: "flex", gap: 1, width: 260 }}>
                <CancelButton />
                <SubmitButton />
              </Box>
            </Stack>
          </Stack>
        </ConfigTabPaper>
      </Card>
    </Box>
  );
};

/**
 * The new-or-edit algo form: product, direction, behaviour and settings.
 *
 * Two shimurai adjustments, and they are independent:
 *
 * - `shimurai` (any selection of them, one included) adds the reference price,
 *   shimurai price and margin fields to the settings row.
 * - `bulk` (more than one) is what actually strips the form back: quantity and
 *   price come off, since a shared value for them across a selection is not
 *   something the desk means to set, and everything but the margin is disabled.
 *   Those fields stay on screen because they are still the context you need to
 *   know *which* algos you are about to retune.
 *
 * So a single shimurai algo edits exactly like any other algo, with one extra
 * field.
 */
type AlgoFieldsProps = {
  quantityFieldRef: React.RefObject<HTMLInputElement | null>;
  marginFieldRef: React.RefObject<HTMLInputElement | null>;
  shimurai: boolean;
  bulk: boolean;
  editing: boolean;
  advancedOpen: boolean;
  /**
   * A LinearCloser terminator: the shimurai pricing fields in the settings,
   * and the margin band and update frequency in the advanced row.
   */
  linearCloser: boolean;
};

/**
 * The settings fields, on a fixed three-column grid: one line for a limit
 * order (quantity, price, tag), and for shimurai the three pricing fields over
 * quantity and a double-width tag. Fixed rather than `auto-fit` because an
 * auto-fit grid put four shimurai fields on the first line and left the tag
 * stranded alone on the second.
 */
const SettingsGrid = (props: PropsWithChildren) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 1,
      // Grid items default to `min-width: auto`, which would let a long value
      // push its column wider than its share.
      "& > *": { minWidth: 0 },
    }}
  >
    {props.children}
  </Box>
);

const AlgoFields = (props: AlgoFieldsProps) => (
  <>
    <Card
      elevation={5}
      sx={{
        padding: 1,
        backgroundColor: (t) => t.palette.background.paper,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          // justifyContent: "space-between",
          flexWrap: "wrap",
          rowGap: 1,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flex: "0 1 110px", minWidth: 60 }}>
            <GridField />
          </Box>
          <Box sx={{ display: "flex", flex: "0 1 175px", minWidth: 155 }}>
            <ProductNameField />
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          {/* An existing algo's direction is fixed; only a new one picks it. */}
          <BuySellToggle disabled={props.bulk || props.editing} />
          <Divider
            orientation="vertical"
            sx={{ alignSelf: "center", height: SMALL_INPUT_HEIGHT }}
          />
          <BehaviorSwitch
            content={Behavior.LIMIT_ORDER}
            disabled={props.bulk}
          />
          <BehaviorSwitch content={Behavior.NURSER} disabled={props.bulk} />
          <TerminatorSwitch disabled={props.bulk} />
          <BehaviorSwitch
            content={Behavior.STEP_SHIMURAI}
            disabled={props.bulk}
          />
        </Box>
      </Stack>
    </Card>
    <Card
      elevation={5}
      sx={{
        padding: 1,
        backgroundColor: (t) => t.palette.background.paper,
      }}
    >
      {props.shimurai || props.linearCloser ? (
        // Pricing first and in derivation order — reference and margin make
        // the price — so the three read left to right as one sum. A
        // LinearCloser shares the layout: its price is display-only too, and
        // the reference price is what it sends (see `toAlgo`).
        <SettingsGrid>
          {props.bulk ? null : (
            // Fixed on a running shimurai, but a LinearCloser's reference is
            // its working price, so it stays editable like any algo's price.
            <ReferencePriceField
              editing={props.editing && !props.linearCloser}
            />
          )}
          <PriceField
            name="margin_override"
            label="Margin override"
            placeholder="Margin"
            required={false}
            inputRef={props.marginFieldRef}
          />
          {props.bulk ? null : <ShimuraiPriceField />}
          {props.bulk ? null : (
            <QuantityField inputRef={props.quantityFieldRef} />
          )}
          {/* Takes the rest of the second line rather than leaving a hole
              beside it. On a bulk override it shares the one line with the
              margin instead. */}
          <Box sx={{ gridColumn: "span 2" }}>
            <AdvancedField name="tag" label="Tag" disabled={props.bulk} />
          </Box>
        </SettingsGrid>
      ) : (
        <SettingsGrid>
          <QuantityField inputRef={props.quantityFieldRef} />
          <PriceField name="price" label="Price" />
          <AdvancedField name="tag" label="Tag" disabled={props.bulk} />
        </SettingsGrid>
      )}
      <Advanced open={props.advancedOpen}>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <RelativeTimeInput
            label="Trade start"
            fieldKey="start"
            clearTooltip="Clear (Trade start will be now)"
            disabled={props.bulk}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <RelativeTimeInput
            label="Trade end"
            fieldKey="end"
            clearTooltip="Clear (Trade end will be when product is no longer tradeable)"
            disabled={props.bulk}
          />
        </Box>

        {props.linearCloser ? (
          <Box sx={{ flex: 1, minWidth: LINEAR_CLOSER_FIELDS_MIN_WIDTH }}>
            <LinearCloserFields />
          </Box>
        ) : null}
        {/* Chosen on creation only — shown disabled on an update, which sends
            the algo's own group back regardless; see `withEchoedBalancingGroup`. */}
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <BalancingGroupField disabled={props.editing || props.bulk} />
        </Box>
      </Advanced>
    </Card>
  </>
);

// why this value actually is a string is beyond me. All types point to numbers even in formik so yeah gg - indeed gg no re
const toNumberOrNull = (value: number | undefined | null | string) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numValue = Number(value);

  if (isNaN(numValue)) {
    return null;
  }

  return numValue;
};

/**
 * The balancing group is chosen when an algo is created and cannot be changed
 * afterwards, but an update must still carry the one the algo already has — the
 * hub may reject or clear a configuration that arrives without it.
 *
 * So an update sends back exactly what the hub echoed on the algo being edited,
 * never a value from the form (which disables the field when editing). An algo
 * created before the field existed echoes `null`; that is omitted rather than
 * sent, matching what every update sent before.
 */
const withEchoedBalancingGroup = (
  configuration: AlgoConfiguration,
  echoed: AlgoConfiguration | undefined
): AlgoConfiguration => {
  const result = { ...configuration };
  delete result.balancing_group;
  if (echoed?.balancing_group != null) {
    result.balancing_group = echoed.balancing_group;
  }
  return result;
};

/**
 * One algo of a bulk shimurai selection, echoed back with only its margin
 * replaced.
 *
 * Deliberately built from the algo rather than from the form: on a bulk
 * override the form's other fields are disabled context, blank wherever the
 * selection disagreed, so every one of them — spec, product definition, the
 * rest of the configuration, the trade window — is passed through untouched.
 * Anything taken from the form here would be an unintended edit to a running
 * algo. A *single* shimurai algo goes through {@link toAlgo} instead, where the
 * whole form is live and meant to be applied.
 */
const toMarginOverrideAlgo = (
  algo: Algo<AlgoConfiguration>,
  values: ConfigTabState
): CreateAlgoRequest => {
  // LT sends all pdefs but this app only ever works with the first, as
  // everywhere else.
  const pdef = algo.product_definitions[0];

  return {
    algo_id: algo.algo_id,
    algo_spec: algo.algo_spec,
    product_definition: {
      delivery_start: dayjs(pdef.delivery_start),
      delivery_end: dayjs(pdef.delivery_end),
      delivery_area_id: pdef.delivery_area_id,
      product_types: pdef.product_types,
    },
    configuration: withEchoedBalancingGroup(
      {
        ...algo.configuration,
        // yen/kWh in the form, yen/MWh on the wire — the same conversion
        // `price` gets in `toAlgo`.
        margin_override:
          Price.fromInput(values.margin_override)?.yenPerMwh ?? null,
      },
      algo.configuration
    ),
    start: algo.start,
    end: algo.end,
  };
};

const toAlgo = (
  values: ConfigTabState,
  initials: string,
  selectedAlgos: Algo<AlgoConfiguration>[]
): CreateAlgoRequest => {
  let configuration: AlgoConfiguration = {
    direction: values.direction!,
    // Scaled on the way out: the trader types yen/kWh, lazerhub takes yen/MWh.
    // `margin_override` below gets the same treatment.
    price: Price.fromInput(values.price)?.yenPerMwh ?? null,
    quantity: toNumberOrNull(values.quantity),
    product_name: values.product_definition.product_name,
    initials,
    tag: values.tag ?? "",
    text: "",
  } satisfies LimitOrderConfiguration;

  const editing = selectedAlgos.length === 1 ? selectedAlgos[0] : undefined;

  let configToKeepFromAlgo = {};

  if (editing) {
    configToKeepFromAlgo = { ...editing.configuration };
  }

  configuration = { ...configToKeepFromAlgo, ...configuration };

  if (values.algo_id === undefined) {
    configuration.balancing_group = values.balancing_group;
  } else {
    configuration = withEchoedBalancingGroup(
      configuration,
      editing?.configuration
    );
  }

  /**
   * A shimurai algo keeps the spec the hub gave it. `behaviorAlgoSpec` has no
   * entry for one — `values.behavior` is `undefined` for a shimurai selection —
   * so deriving the name from the buttons below would rename a running shimurai
   * algo into a plain limitorder or nurser.
   */
  const shimuraiSpec =
    editing && isShimuraiAlgoSpec(editing.algo_spec.name)
      ? editing.algo_spec
      : undefined;

  if (!shimuraiSpec && values.behavior === Behavior.TERMINATOR) {
    // `SubmitButton` stays disabled until one is picked, so this never sends
    // `undefined`.
    configuration = {
      ...configuration,
      behaviors: values.terminator_behavior!,
    } satisfies TerminatorConfiguration;
  }

  if (!shimuraiSpec && isLinearCloser(values)) {
    // The algo trades off the reference price alone. The form's price is
    // derived from reference and margin for display only, as on a shimurai,
    // so LT is sent the reference as `price` too.
    const referencePrice =
      Price.fromInput(values.reference_price)?.yenPerMwh ?? null;
    configuration = {
      ...configuration,
      price: referencePrice,
      reference_price: referencePrice,
      // All yen/kWh in the form, yen/MWh on the wire, like `price`.
      margin_override:
        Price.fromInput(values.margin_override)?.yenPerMwh ?? null,
      min_margin: Price.fromInput(values.min_margin)?.yenPerMwh ?? null,
      max_margin: Price.fromInput(values.max_margin)?.yenPerMwh ?? null,
      upper_omt_update_frequency: toNumberOrNull(
        values.upper_omt_update_frequency
      ),
    };
  }

  if (shimuraiSpec || values.behavior === Behavior.STEP_SHIMURAI) {
    configuration = {
      ...configuration,
      // yen/kWh in the form, yen/MWh on the wire — the same conversion `price`
      // above gets.
      margin_override:
        Price.fromInput(values.margin_override)?.yenPerMwh ?? null,
      reference_price:
        Price.fromInput(values.reference_price)?.yenPerMwh ?? null,
    };
  }

  return {
    algo_id: values.algo_id ?? uuidv4(),
    algo_spec: shimuraiSpec ?? {
      name: behaviorAlgoSpec[values.behavior!],
      version: "",
    },
    product_definition: {
      delivery_start: dayjs(values.product_definition.delivery_start),
      delivery_end: dayjs(values.product_definition.delivery_end),
      delivery_area_id: values.product_definition.grid,
      product_types: values.product_definition.prefilled_product_types,
    },
    configuration,
    start: values.start === undefined ? undefined : values.start.toISOString(),
    end: values.end === undefined ? undefined : values.end.toISOString(),
  };
};

export { ConfigTab };
